import React, {createContext, useContext, useState, useCallback, useEffect} from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ConfirmOptions {
    title: string;
    message?: string;
    content?: React.ReactNode;
    okCaption?: string;
    cancelCaption?: string;
}

interface DialogQueueItem {
    type: 'confirm' | 'alert';
    opts: ConfirmOptions;
    title?: string;
    message?: string;
    resolve: (value: boolean | void) => void;
}

interface DialogContextType {
    confirm: (options: ConfirmOptions) => Promise<boolean>;
    alert: (title: string, message?: string) => Promise<void>;
}

const DialogContext = createContext<DialogContextType>({
    confirm: () => Promise.resolve(false),
    alert: () => Promise.resolve(),
});

export const useDialog = () => useContext(DialogContext);

export const DialogProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmOptions>({title: ''});
    const [isAlert, setIsAlert] = useState(false);
    const [dialogQueue, setDialogQueue] = useState<DialogQueueItem[]>([]);

    const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            setDialogQueue((prev) => [...prev, {type: 'confirm', opts, resolve}]);
        });
    }, []);

    const alert = useCallback((title: string, message?: string): Promise<void> => {
        return new Promise<void>((resolve) => {
            setDialogQueue((prev) => [...prev, {type: 'alert', opts: {title, message}, title, message, resolve}]);
        });
    }, []);

    // Show the first dialog in the queue if none is currently open
    useEffect(() => {
        if (dialogQueue.length > 0 && !open) {
            const current = dialogQueue[0];
            setOptions(current.opts);
            setIsAlert(current.type === 'alert');
            setOpen(true);
        }
    }, [dialogQueue, open]);

    const handleClose = (result: boolean) => {
        setOpen(false);
        const current = dialogQueue[0];
        if (current) {
            current.resolve(current.type === 'alert' ? undefined : result);
            // Remove the processed dialog from the queue
            setDialogQueue((prev) => prev.slice(1));
        }
    };

    return (
        <DialogContext.Provider value={{confirm, alert}}>
            {children}
            <Dialog open={open} onClose={() => handleClose(false)}>
                <DialogTitle sx={{whiteSpace: 'break-spaces'}}>
                    {options.title}
                </DialogTitle>
                <IconButton
                    onClick={() => handleClose(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}>
                    <CloseIcon />
                </IconButton>
                {(options.message || options.content) && (
                    <DialogContent>
                        {options.content || (
                            <Typography
                                variant="body1"
                                style={{textAlign: 'center', whiteSpace: 'break-spaces'}}>
                                {options.message}
                            </Typography>
                        )}
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => handleClose(true)} autoFocus>
                        {options.okCaption || 'OK'}
                    </Button>
                    {!isAlert && (
                        <Button color="secondary" onClick={() => handleClose(false)}>
                            {options.cancelCaption || 'Cancel'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </DialogContext.Provider>
    );
};
